<template>
	<div class="k-date-picker">
		<datepicker
			v-model="value"
			language="pl"
			:name="name"
			:class="[{'k-date-picker__error': 0 != errors.length}]"
			:placeholder="placeholder"
			:disabled-dates="disabledDates"
			:minimum-view="minView"
			:maximum-view="maxView"
			format="dd.MM.yyyy"
		>
			<template v-slot:customCalendarHeader>
				<slot name="customCalendarHeader"></slot>
			</template>
			<template v-slot:belowDate>
				<slot name="belowDate"></slot>
			</template>
		</datepicker>
		<div v-if="!hideErrors" class="k-date-picker__errors">{{ errors[0] }}</div>
		<div v-if="clearable && null != value" @click="clearAction" class="k-date-picker__clearable" :class="{'k-date-picker__clearable--hide-errors': hideErrors}">
			<KIcon icon="close" :width="clearableWidth" :height="clearableHeight" :color="clearableColor" />
		</div>
	</div>
</template>

<script setup lang="ts">
import Datepicker from 'vuejs3-datepicker';
import { useField } from 'vee-validate';
import { computed, defineEmits, defineProps, toRef, withDefaults } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import KIcon from '@/components/atoms/KIcon.vue';

type dateView = 'day' | 'month' | 'year';
interface IOwnProps {
	name: string
	modelValue?: string | null | Dayjs
	placeholder?: string
	disabledDates?: Object
	clearable?: boolean
	hideErrors?: boolean
	clearableColor?: string
	clearableWidth?: string
	clearableHeight?: string
	minView?: dateView
	maxView?: dateView
}
interface IProps extends IOwnProps {
}

const props = withDefaults(defineProps<IProps>(), {
	clearableColor: '#000',
	clearableWidth: '16',
	clearableHeight: '16',
});
const emit = defineEmits(['update:modelValue']);

const { errors } = useField(toRef(props, 'name'), undefined);

const value = computed({
	get() {
		return props.modelValue;
	},
	set(value) {
		emit('update:modelValue', dayjs(value).format('YYYY.MM.DD HH:mm:ss'));
	}
});

const clearAction = () => {
	emit('update:modelValue', null);
};
</script>

<style scoped lang="scss">
.k-date-picker {
	position: relative;

	&__errors {
		min-height: 25px;
		padding: 3px 15px;
		font-size: 12px;
		color: $red-400;
		text-transform: lowercase;

		&::first-letter {
			text-transform: uppercase;
		}
	}

	&:deep(.vuejs3-datepicker) {
		width: 100%;
	}

	&:deep(.vuejs3-datepicker__icon) {
		height: 16px;
	}

	&:deep(.vuejs3-datepicker__calendar-actionarea) {
		background-color: transparent;
		color: $black;
	}

	&:deep(.vuejs3-datepicker__content) {
		font-size: 14px;
		font-weight: 300;
		color: $black;
		letter-spacing: 0;
		margin-left: 5px;
	}

	&:deep(.vuejs3-datepicker__value) {
		display: flex;
		align-items: center;
		padding: 11.76px 10px;
		width: 100%;
		background-color: transparent;
		min-width: 115px;
		transition: border-color 0.3s;

		&:hover {
			border-color: $primary-400;
		}
	}

	&__error {
		&:deep(.vuejs3-datepicker__value) {
			border-color: $red-400;
		}
	}

	&__clearable {
		position: absolute;
		top: 50%;
		right: 10px;
		cursor: pointer;
		width: max-content;
		transform: translateY(-100%);
		transition: opacity 0.3s;
		animation: showClearBtn 0.5s;

		&:hover {
			opacity: 0.5;
		}

		@keyframes showClearBtn {
			from {
				opacity: 0;
			}

			to {
				opacity: 1;
			}
		}

		&--hide-errors {
			transform: translateY(-45%);
		}
	}

	&:deep(.cell.selected) {
		background: $primary-400;

		&:hover {
			border: 1px solid $primary-400;
			background: $primary-800;
		}
	}

	&:deep(.vuejs3-datepicker__calendar header .up:not(.disabled):hover) {
		&:hover {
			background-color: $primary-400;
		}
	}

	&:deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover) {
		&:hover {
			border: 1px solid $primary-400;
		}
	}

	&:deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover) {
		&:hover {
			border: 1px solid $primary-400;
		}
	}

	&:deep(.vuejs3-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover) {
		&:hover {
			border: 1px solid $primary-400;
		}
	}

	&:deep(.vuejs3-datepicker__calendar-topbar-year) {
		padding: 0;
	}

	&:deep(.vuejs3-datepicker__calendar-topbar) {
		background: $primary-400;
		padding: 3px 0;
		font-size: 26px;
	}

	&:deep(.vuejs3-datepicker__calendar-topbar-day) {
		font-size: 18px;
	}

	&:deep(.vuejs3-datepicker__calendar header .prev:after) {
		border-bottom: 1px solid $black;
		border-left: 1px solid $black;
	}

	&:deep(.vuejs3-datepicker__calendar header .next:after) {
		border-top: 1px solid $black;
		border-right: 1px solid $black;
	}

	&:deep(.vuejs3-datepicker *) {
		fill: $black;
	}

	&:deep(.disabled) {
		color: $grey-400;
	}

	svg {
		fill: $black;
	}

	&:deep(.vuejs3-datepicker #calendar-div) {
		background-color: $primary-400;
	}
}
</style>
